import { Anchor, Box, Center, Divider, Group, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import type { FC } from 'react';
import { Link } from 'react-router';

import { ButtonRow } from '../../components/global/ButtonRow/ButtonRow.tsx';
import { Panel } from '../../components/global/Panel/Panel.tsx';
import { usePromoterItemQuery } from '../../data/promoter/usePromoterItemQuery.ts';
import { usePromoterItemUpdateMutation } from '../../data/promoter/usePromoterItemUpdateMutation.ts';
import { useUserQuery } from '../../data/user/useUserQuery.ts';
import { useUserUpdateMutation } from '../../data/user/useUserUpdateMutation.ts';
import type { FormUserAndPromoter } from '../../forms/UserAndPromoter/schema.ts';
import { UserAndPromoterForm } from '../../forms/UserAndPromoter/UserAndPromoterForm.tsx';
import { useI18n } from '../../hooks/useI18n.ts';

export const Account: FC = () => {
  const { t } = useI18n();

  const { data: userData } = useUserQuery();
  const { data: promoterData } = usePromoterItemQuery();

  const { mutate: mutatePromoter, isPending: promoterIsPending } = usePromoterItemUpdateMutation();
  const { mutate: mutateUser, isPending: userIsPending } = useUserUpdateMutation();

  const onError = () =>
    notifications.show({
      color: 'gigi-apple-pink',
      message: t('account_notification--save-error') ?? 'Your details could not be saved. Please try again.',
      title: t('account_notification-title--save-error') ?? 'Error Saving',
    });

  const onSubmit = ({ firstName, lastName, organisationName, phoneNumber }: FormUserAndPromoter) => {
    // The name lives on the account, the organisation and phone number on the promoter record,
    // so saving takes two calls. The promoter update only runs once the user update succeeded,
    // the same order the onboarding step uses.
    mutateUser(
      { first_name: firstName, last_name: lastName },
      {
        onError,
        onSuccess: () => {
          mutatePromoter(
            { name: organisationName, phone_number: phoneNumber },
            {
              onError,
              onSuccess: () =>
                notifications.show({
                  message: t('account_notification--saved') ?? 'Your details have been saved.',
                  title: t('account_notification-title--saved') ?? 'Details Saved',
                }),
            },
          );
        },
      },
    );
  };

  if (!userData || !promoterData) return null;

  const defaultValues = {
    firstName: userData.first_name ?? '',
    lastName: userData.last_name ?? '',
    organisationName: promoterData.name ?? '',
    phoneNumber: promoterData.phone_number ?? '',
  };

  return (
    <Box py="xl" w="100%">
      <Center>
        <Stack gap="xs">
          <Anchor c="gigi-grey-text" component={Link} size="xxs" to="/dashboard" underline="never">
            <Group gap="xxxs">
              <ArrowLeftIcon size={18} />
              {t('button--back-to-dashboard') ?? 'Go back to Dashboard'}
            </Group>
          </Anchor>

          <Panel>
            <Stack gap="xxs">
              <Title order={1}>{t('account_title') ?? 'Account settings'}</Title>
              <Text c="gigi-grey-text" size="xxs">
                {t('account_subtitle') ?? 'Update your name, organisation and phone number.'}
              </Text>
            </Stack>

            <UserAndPromoterForm
              buttonSlot={
                <ButtonRow
                  primaryButton={{
                    isPending: userIsPending || promoterIsPending,
                    text: t('account_button--save') ?? 'Save changes',
                    type: 'submit',
                  }}
                />
              }
              defaultValues={defaultValues}
              onSubmit={onSubmit}
            />

            <Divider />

            {/*
              Deliberately not an input: the e-mail address is also the login, so changing it
              would need a verification round trip before it may take effect. Rendering it as a
              field that looks editable but is not invites people to try.
            */}
            <Stack gap={2}>
              <Text c="gigi-grey-text" size="xxs">
                {t('account_field-label--email') ?? 'Email address'}
              </Text>
              <Text fw={500} size="xs">
                {userData.email}
              </Text>
              <Text c="gigi-grey-text" size="xxs">
                {t('account_field-description--email') ??
                  'Your e-mail address is also your login. Contact GoGiGi to have it changed.'}
              </Text>
            </Stack>
          </Panel>
        </Stack>
      </Center>
    </Box>
  );
};
